import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { chatbotService } from "../../services/chatbotService";
import { tenantService } from "../../services/tenantService";
import { Chatbot, Tenant } from "../../types";

export default function ChatbotList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tenantService
      .list()
      .then((res) => setTenants(res.data.data.tenants))
      .catch((e) => {
        console.error(e);
        setError("Errore nel caricamento dei tenant");
      });
  }, []);

  useEffect(() => {
    if (!tenantIdParam) {
      setChatbots([]);
      return;
    }
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await chatbotService.list(tenantIdParam);
        setChatbots(res.data.data.chatbots);
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento dei chatbot");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tenantIdParam]);

  const tenantName = (id: string) =>
    tenants.find((t) => t.id === id)?.name ?? id;

  return (
    <div className="container">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <FormLabel label="Lista Chatbot" cols={2} />
        <Button
          style={{ flex: "1 1 20%" }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/chatbot/chatbots/new")}
        >
          Nuovo chatbot
        </Button>
      </Box>

      <Box sx={{ mb: 2, maxWidth: 320 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="tenant-filter">Filtra per tenant</InputLabel>
          <Select
            labelId="tenant-filter"
            label="Filtra per tenant"
            value={tenantIdParam}
            onChange={(e) =>
              setSearchParams(
                e.target.value ? { tenantId: e.target.value } : {},
              )
            }
          >
            <MenuItem value="">— seleziona tenant —</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!tenantIdParam && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Seleziona un tenant per visualizzare i chatbot.
        </Alert>
      )}

      {tenantIdParam &&
        (loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Modello</TableCell>
                  <TableCell>Flow</TableCell>
                  <TableCell>Creato il</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chatbots.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nessun chatbot trovato
                    </TableCell>
                  </TableRow>
                )}
                {chatbots.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {c.id.slice(0, 8)}…
                    </TableCell>
                    <TableCell>{tenantName(c.tenant_id)}</TableCell>
                    <TableCell>{c.model}</TableCell>
                    <TableCell>
                      {c.flow_id ? c.flow_id.slice(0, 8) + "…" : "—"}
                    </TableCell>
                    <TableCell>
                      {new Date(c.created_at).toLocaleDateString("it-IT")}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Dettaglio">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/chatbot/chatbots/${c.id}`)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
    </div>
  );
}
